import classNames from 'classnames'
import { RootState } from 'duck/store'
import { Corporate } from 'entities-next/corporate'
import React, { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import CriteriaAssessmentsView from 'scenes/Dashboard/components/AssessmentsView/presentational'
import { DealCardCompanyBlurb } from 'scenes/Dashboard/components/DealCardCompanyBlurb'
import { DealCardCompanyInvestorsList } from 'scenes/Dashboard/components/DealCardCompanyInvestorsList'
import { DealCardCompanyLogo } from 'scenes/Dashboard/components/DealCardCompanyLogo'
import { DealCardCompanyName } from 'scenes/Dashboard/components/DealCardCompanyName'
import { DealCardSignalAssessmentList } from 'scenes/Dashboard/components/DealCardSignalAssessmentList'
import BasicOrganizationInfo from 'scenes/Dashboard/components/OrganizationDetailsModal/components/OrganizationDetailsView/components/BasicOrganizationInfo'
import { showOrganizationDetailsAsAdmin } from 'scenes/Dashboard/components/OrganizationDetailsModal/duck/actions'
import { Analysis } from 'scenes/Dashboard/data/analyses/analyses.types'
import { getDeal } from 'scenes/Dashboard/data/deals/selectors'
import { Deal } from 'scenes/Dashboard/data/deals/types'
import { getOrganization } from 'scenes/Dashboard/data/organizations/selectors'
import { Organization } from 'scenes/Dashboard/data/organizations/types'
import { makeStyles } from 'tss-react/mui'

import { Grid, Paper, Theme, Typography } from '@mui/material'

import {
  getOrganizationFacebookUrl,
  getOrganizationLinkedinUrl,
  getOrganizationTwitterUrl,
  getOrganizationWebsiteUrl,
} from '../../../../data/digitalPresences/helpers'
import { PairingActions } from '../PairingActions.component'
import { DealCardAdminNotes } from './DealCardAdminNotes.component'
import { DealCardFitUrl } from './DealFitUrl.component'
import { DealIsCalibration } from './DealIsCalibration.component'
import { DealIsValid } from './DealIsValid.component'
import { DealRelaxedSweetSpot } from './DealRelaxedSweetSpot.component'
import { DealRelaxedSweetSpotDefinition } from './DealRelaxedSweetSpotDefinition.component'
import { DealSearchFunction } from './DealSearchFunction'
import { DealInfoHeader } from './DealSearchFunction/DealInfoHeader.component'
import { DealFitRelevanceSelector } from './DealFitRelevanceSelector.component'

export type DealCardProps = {
  className?: string
  style?: React.CSSProperties
  orderedDeal: Deal
  analysis?: Analysis
  corporate?: Corporate
}

export const DealCard = React.memo(({ analysis, corporate, className, orderedDeal }: DealCardProps) => {
  const { classes } = useStyles()
  const deal = orderedDeal
  const { organization } = useDealCardData(deal.id)
  const { showDetails } = useDealCardHandlers(deal)
  const handleClick = showDetails
  const urls = {
    website: getOrganizationWebsiteUrl(organization),
    linkedin: getOrganizationLinkedinUrl(organization),
    facebook: getOrganizationFacebookUrl(organization),
    twitter: getOrganizationTwitterUrl(organization),
  }

  const relaxedSweetSpotDefinition = deal.searchFunction ? deal.searchFunction.relaxedSweetSpotDefinition ?? '' : ''
  const companiesWithoutFundingInfo =
    deal.searchFunction && deal.searchFunction.includeCompaniesWithoutMaturityInformation ? 'True' : 'False'

  return (
    <Paper elevation={2} className={classNames(className, classes.root)}>
      <Grid container>
        <Grid item key="organization-summary" md={6} xs={12} className={classes.organizationInfoContainer}>
          <Grid item key="organization-summary-info" md={6} xs={12} className={classes.organizationInfoWidth}>
            <section className={classNames(classes.alignedRow, classes.organizationLogoAndName)}>
              <DealCardCompanyLogo
                className={classes.logoContainer}
                organization={organization}
                onClick={handleClick}
              />
              <DealCardCompanyName organization={organization} urls={urls} />
            </section>
            <DealCardCompanyBlurb className={classes.organizationBlurb} organization={organization} />
          </Grid>
        </Grid>
        <Grid item key="investor-summary" md={6} xs={12} className={classes.investorInfoContainer}>
          <DealInfoHeader searchFunction={deal.searchFunction} corporate={corporate} />
        </Grid>
      </Grid>
      <Grid container>
        <Grid item key="organization-details" md={6} xs={12} className={classes.organizationInfoContainer}>
          <BasicOrganizationInfo organization={organization} />
          <DealCardCompanyInvestorsList organization={organization} />
          <DealCardSignalAssessmentList organization={organization} />
          {!!deal.fitUrl && <DealCardFitUrl className={classes.fitUrlSection} fitUrl={deal.fitUrl} />}
        </Grid>
        <Grid item key="investor-details" md={6} xs={12} className={classes.investorInfoContainer}>
          {analysis && <CriteriaAssessmentsView summary analysis={analysis} deal={deal} />}
          <div>
            <Typography className={classes.title} variant="body1" gutterBottom>
              Companies without funding info
            </Typography>
            <span> {companiesWithoutFundingInfo}</span>
          </div>
          <br />
          <DealRelaxedSweetSpotDefinition relaxedSweetSpotDefinition={relaxedSweetSpotDefinition} />
          <br />
          <DealSearchFunction deal={deal} />
          <DealRelaxedSweetSpot className={classes.infoSection} deal={deal} />
          <DealIsCalibration className={classes.infoSection} deal={deal} />
          <br />
          <DealFitRelevanceSelector deal={deal} />
          <br />

          <p>
            Organization Manually edited: <strong>{getProcessedByOps(organization)}</strong>
          </p>
          <p className={classes.infoSection}>
            <DealIsValid deal={deal} /> updated at: {deal?.isValidUpdatedAt ?? 'never'}
          </p>
          {corporate && !!corporate.adminNotes && (
            <DealCardAdminNotes className={classes.infoSection} adminNotes={corporate.adminNotes} />
          )}
        </Grid>
      </Grid>
      <div className={classes.dealActionsContainer}>
        <PairingActions deal={deal} organization={organization!} />
      </div>
    </Paper>
  )
})

DealCard.displayName = 'DealCard'

function useDealCardData(dealId: Deal['id']) {
  const deal = useSelector((state: RootState) => getDeal(state, dealId))

  if (!deal) {
    throw new Error('Undefined deal')
  }
  const source = useMemo(() => deal.source, [deal])
  const analysis = deal.analysis || undefined
  const organization = useSelector((state: RootState) => getOrganization(state, deal.organizationId))
  if (!organization) {
    throw new Error('Organization not available')
  }

  return {
    deal,
    source,
    analysis,
    organization,
  }
}

function useDealCardHandlers(deal: Deal) {
  const dispatch = useDispatch()
  const showDetails = useCallback(() => dispatch(showOrganizationDetailsAsAdmin({ dealId: deal.id })), [
    dispatch,
    deal.id,
  ])
  return { showDetails }
}

const useStyles = makeStyles()(({ typography, spacing }: Theme) => ({
  root: {
    padding: spacing(2),
  },
  alignedRow: {
    display: 'flex',
    alignItems: 'center',
  },
  highlightedInvestor: {
    backgroundColor: '#fff9de',
  },
  Accordion: {
    background: 'transparent',
    cursor: 'auto',
    userSelect: 'auto',
  },
  AccordionSummary: {
    cursor: 'auto',
    userSelect: 'auto',
  },
  expandIcon: {
    position: 'absolute',
    bottom: 0,
    right: 0,
  },
  onHoldFlagContainer: {
    position: 'absolute',
    right: spacing(-0.5),
    top: spacing(-0.5),
  },
  organizationInfoContainer: {
    marginTop: spacing(2),
    paddingRight: spacing(),
  },
  organizationInfoWidth: {
    maxWidth: '100%',
  },
  organizationLogoAndName: {
    marginBottom: spacing(0.25),
    alignItems: 'start',
  },
  chipsContainer: {
    marginTop: spacing(2),
    marginBottom: spacing(1),
    '& > *:not(:last-child)': {
      marginRight: spacing(0.5),
    },
  },
  investorInfoContainer: {
    marginTop: spacing(2),
    paddingLeft: spacing(),
    paddingBottom: spacing(0),
  },
  infoSection: {
    marginBottom: spacing(2),
  },
  fitUrlSection: {
    marginTop: spacing(2),
  },
  logoContainer: {
    marginRight: spacing(2),
  },
  matchingScoreContainer: {
    marginRight: spacing(),
  },
  organizationBlurb: {
    marginTop: spacing(2),
    textAlign: 'justify',
  },
  investorContainer: {
    marginRight: spacing(),
    justifyContent: 'space-between',
  },
  dealActionsContainer: {
    marginTop: spacing(2),
  },
  investorInfoLeft: {
    flex: '1 1 auto',
  },
  organizationExtraInfo: {
    textAlign: 'right',
  },
  dataCell: {
    textAlign: 'left',
    paddingLeft: spacing(0.5),
  },
  title: {
    fontWeight: typography.fontWeightBold,
  },
}))

function getProcessedByOps(organization?: Organization) {
  return organization?.processedByOps === true ? 'Yes' : 'No'
}
